export function parseEduWorkContentItems(text) {
  if (!text) return []
  return String(text)
    .split(/[；;]\r?\n|[；;](?![\r\n])/g)
    .map((item) =>
      String(item || '')
        .replace(/^[；;,\s]+|[；;,\s]+$/g, '')
        .trim()
    )
    .filter(Boolean)
}

export function labelToPath(label) {
  return String(label || '')
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean)
}

function cloneChildren(children) {
  const seen = {}
  const list = []
  ;(children || []).forEach((child) => {
    const label = String((child && child.label) || '').trim()
    if (!label || seen[label]) return
    seen[label] = true
    list.push({ label })
  })
  return list
}

export function normalizeTeamWorkGroups(groups) {
  const list = mergeWorkContentTrees(
    (groups || []).map((item) => ({
      label: String((item && item.label) || '').trim(),
      children: item && item.children
    })),
    []
  )
  return {
    labels: list.map((item) => item.label),
    groups: list,
    paths: list.map((item) => labelToPath(item.label)).filter((path) => path.length)
  }
}

export function parseTeamWorkContent(raw) {
  const empty = { labels: [], groups: [], paths: [] }
  if (raw == null || raw === '') return empty
  if (Array.isArray(raw)) {
    return normalizeTeamWorkGroups(
      raw.map((item) =>
        typeof item === 'string' ? { label: item, children: [] } : item
      )
    )
  }
  if (typeof raw === 'object') {
    if (Array.isArray(raw.groups)) return normalizeTeamWorkGroups(raw.groups)
    if (Array.isArray(raw.labels)) {
      return normalizeTeamWorkGroups(
        raw.labels.map((label) => ({ label, children: [] }))
      )
    }
    if (raw.label) return normalizeTeamWorkGroups([raw])
    return empty
  }
  const text = String(raw).trim()
  if (!text) return empty
  if (text.charAt(0) === '{' || text.charAt(0) === '[') {
    try {
      return parseTeamWorkContent(JSON.parse(text))
    } catch (e) {
      return empty
    }
  }
  return normalizeTeamWorkGroups(
    parseEduWorkContentItems(text).map((label) => ({ label, children: [] }))
  )
}

export function stringifyTeamWorkContent(groups) {
  const parsed = normalizeTeamWorkGroups(groups)
  if (!parsed.groups.length) return ''
  return JSON.stringify({
    labels: parsed.labels,
    groups: parsed.groups
  })
}

export function teamWorkContentText(raw) {
  return parseTeamWorkContent(raw).labels.join('；\n')
}

export function matchWorkTreeByLabels(tree, labels) {
  const set = {}
  ;(labels || []).forEach((label) => {
    if (label) set[label] = true
  })
  return (tree || []).filter((item) => item && set[item.label])
}

export function expandWorkLabelsToGroups(labels, tree) {
  const matched = matchWorkTreeByLabels(tree, labels)
  const found = {}
  matched.forEach((item) => {
    found[item.label] = true
  })
  const extra = (labels || [])
    .filter((label) => label && !found[label])
    .map((label) => ({ label, children: [] }))
  return matched.concat(extra)
}

export function mergeWorkContentTrees(primary, extra) {
  const map = {}
  const list = []
  const add = (item) => {
    if (!item) return
    const label = String(item.label || '').trim()
    if (!label) return
    if (map[label]) {
      const exist = map[label]
      exist.children = cloneChildren((exist.children || []).concat(item.children || []))
      return
    }
    map[label] = Object.assign({}, item, {
      label,
      children: cloneChildren(item.children)
    })
    list.push(map[label])
  }
  ;(primary || []).forEach(add)
  ;(extra || []).forEach(add)
  return list
}

export function uniqueWorkContentTree(tree) {
  return mergeWorkContentTrees(tree, [])
}

export function isSubsetWorkTree(filtered, all) {
  if (!filtered || !filtered.length || !all || !all.length) return false
  if (filtered.length >= all.length) return false
  const allSet = {}
  all.forEach((item) => {
    if (item && item.label) allSet[item.label] = true
  })
  return filtered.every((item) => item && allSet[item.label])
}

export function teamWorkGroupsFromTeam(team, tree) {
  if (!team) return []
  const parsed = parseTeamWorkContent(team.workContent || team.workcontent)
  if (parsed.groups.some((item) => item.children && item.children.length)) {
    return parsed.groups
  }
  if (parsed.labels.length && tree && tree.length) {
    return expandWorkLabelsToGroups(parsed.labels, tree)
  }
  return parsed.groups
}

export function applyTeamWorkContentToForm(form, team, tree, fillEdu) {
  if (!form) return false
  const groups = teamWorkGroupsFromTeam(team, tree)
  if (!groups.length) return false
  if (typeof fillEdu === 'function') {
    fillEdu(form, groups)
  }
  return true
}

export function pathsToWorkLabels(paths) {
  return (paths || [])
    .map((item) => {
      if (Array.isArray(item)) {
        return item.filter(Boolean).join('/')
      }
      return String(item || '').trim()
    })
    .filter(Boolean)
}

export function nestEduWorkTree(rows) {
  const roots = []
  const source = rows || []
  source.forEach((item) => {
    const parts = []
    String(item.label || '')
      .split('/')
      .forEach((part) => {
        const text = part.trim()
        if (text) parts.push(text)
      })
    if (!parts.length) {
      const typeParts = [item.riskProType, item.riskWokeType, item.riskSource]
      typeParts.forEach((part) => {
        const text = String(part || '').trim()
        if (text) parts.push(text)
      })
    }
    if (!parts.length) return
    if (parts.some((part) => /^[A-Za-z0-9_]+$/.test(part) && /[A-Za-z]/.test(part))) return
    let list = roots
    let node = null
    parts.forEach((part, index) => {
      node = list.find((row) => row.label === part)
      if (!node) {
        node = { label: part, value: part, children: [] }
        list.push(node)
      }
      if (index === parts.length - 1) {
        const prevKids = (node.children || []).slice()
        const seen = {}
        const children = []
        prevKids.concat(item.children || []).forEach((child) => {
          const childLabel = String((child && child.label) || '').trim()
          if (!childLabel || seen[childLabel]) return
          seen[childLabel] = true
          children.push(Object.assign({}, child, { label: childLabel }))
        })
        node.source = Object.assign({}, item, { children })
        node.value = part
        delete node.children
      } else {
        if (!node.children) node.children = []
        list = node.children
      }
    })
  })
  return roots
}
